import { ErrorMessage, SuccessMessage } from "#root/components/alert";
import { disappear } from "#root/components/disappear";
import { Form } from "#root/components/form";
import { GlyphLoading } from "#root/components/glyphs";
import type { MaybePromise } from "#root/types";
import { useData } from "#root/utility/hooks";
import type RemoteCollection from "#root/utility/remote-collection";
import type { RecordAddResponse, RecordDeleteResponse, RecordUpdateResponse } from "#root/utility/remote-data";
import type { ReactNode } from "react";
import { createContext, useCallback, useContext, useMemo, useState } from "react";
import type { z, ZodType } from "zod";

export type EditorContextType = {
  id: string | number | null;
  deleteRecord: () => Promise<void>;
};

export const EditorContext = createContext<EditorContextType | undefined>(undefined);

export type GenericEditorProps<ListRowType extends ZodType, DetailRowType extends ListRowType> = {
  name?: string;
  id?: number | string | null | undefined;
  children: (opts: { id: number | string | null | undefined; message: ReactNode }) => ReactNode;
  collection: RemoteCollection<ListRowType, DetailRowType>;
  initialData?: Record<string, unknown>;
  onDelete?: (data: RecordDeleteResponse<z.output<DetailRowType>> | RecordUpdateResponse<z.output<DetailRowType>>) => MaybePromise<void>;
  onSave?: (data: RecordAddResponse<z.output<DetailRowType>> | RecordUpdateResponse<z.output<DetailRowType>>) => MaybePromise<void>;
  dirty?: boolean;
  className?: string;
};

export function useEditor() {
  return useContext(EditorContext);
}

export default function GenericEditor<ListRowType extends ZodType, DetailRowType extends ListRowType>({
                                                                                                        name = "Record",
                                                                                                        id,
                                                                                                        collection,
                                                                                                        initialData,
                                                                                                        onDelete,
                                                                                                        onSave,
                                                                                                        dirty,
                                                                                                        children,
                                                                                                        className,
                                                                                                      }: GenericEditorProps<ListRowType, DetailRowType>) {
  const [version, setVersion] = useState(0);
  const [message, setMessage] = useState<ReactNode | null>(null);
  const [disabled, setDisabled] = useState(false);

  if (id === "new") id = null;

  const [details, setDetails] = useData(async () => {
    if (initialData) {
      return { ...initialData };
    }

    if (!id) return {};

    const { row } = await collection.details(id);
    return row;
  }, [id, collection, initialData]);

  const deleteRecord = useCallback(async () => {
    try {
      setDisabled(true);
      if (!id) {
        console.log("No id to delete");
        return;
      }
      const response = await collection.delete(id);

      setDetails(response.row ?? {});
      setMessage(disappear(<SuccessMessage>{name} has been delete</SuccessMessage>));

      onDelete?.(response);

      setVersion(version + 1);
    } catch (error) {
      console.log("Error while running delete handler:", error);
      setMessage(disappear(<ErrorMessage error/>));
    }

    setDisabled(false);
  }, [id, collection, onDelete]);

  const submit = useCallback(
    async data => {
      try {
        setDisabled(true);

        const response = await collection.addOrUpdate(id, data);

        setDetails(response.row);
        setMessage(
          disappear(
            <SuccessMessage>
              {name} has been {id === "new" ? "created" : "updated"}
            </SuccessMessage>,
          ),
        );

        onSave?.(response);

        setVersion(version + 1);
      } catch (error) {
        console.log("Error while running create/update handler:", error);
        setMessage(disappear(<ErrorMessage error/>));
      }

      setDisabled(false);
    },
    [name, setDetails, setMessage, setVersion, id, collection, version, onSave],
  );

  const context = useMemo(
    () => ({
      id: id ?? null,
      deleteRecord,
    }),
    [id, deleteRecord],
  );

  //useImperativeHandle(ref,() => context,[context]);

  if (!details) {
    return <GlyphLoading/>;
  }

  return (
    <EditorContext.Provider value={context}>
      <Form key={`${id}-${version}`} data={details} disabled={disabled} dirty={dirty} onSubmit={submit} className={className}>
        {children({ message, id })}
      </Form>
    </EditorContext.Provider>
  );
}
